import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import SkalaLogo from "../components/SkalaLogo";
import { Check, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

export default function PendingPage() {
    const { user, logout } = useAuth();
    const { language } = useLanguage();
    const copy = {
        en: {
            received: "Application received",
            thanks: user ? `Thanks, ${user.full_name.split(" ")[0]}.` : "Thanks for applying.",
            prep: "Your panel is being prepared.",
            body: "Our team is reviewing your submission. You'll receive panel access and onboarding instructions via email within 24 hours.",
            s1: "Check your inbox for our review email.",
            s2: "Sign service agreement, choose plan.",
            s3: "Slack invite + first ad accounts in < 5 min.",
            back: "Back to homepage",
            out: "Sign out",
        },
        pt: { received: "Solicitação recebida", thanks: user ? `Obrigado, ${user.full_name.split(" ")[0]}.` : "Obrigado por se candidatar.", prep: "Seu painel está sendo preparado.", body: "Nossa equipe está revisando seu envio. Você receberá acesso e instruções por e-mail em até 24 horas.", s1: "Verifique seu e-mail para nossa revisão.", s2: "Assine o contrato e escolha o plano.", s3: "Convite Slack + primeiras contas em < 5 min.", back: "Voltar para home", out: "Sair" },
        es: { received: "Solicitud recibida", thanks: user ? `Gracias, ${user.full_name.split(" ")[0]}.` : "Gracias por aplicar.", prep: "Tu panel se está preparando.", body: "Nuestro equipo está revisando tu solicitud. Recibirás acceso e instrucciones por correo en 24 horas.", s1: "Revisa tu correo para nuestra respuesta.", s2: "Firma el acuerdo y elige plan.", s3: "Invitación Slack + primeras cuentas en < 5 min.", back: "Volver al inicio", out: "Cerrar sesión" },
        ru: { received: "Заявка получена", thanks: user ? `Спасибо, ${user.full_name.split(" ")[0]}.` : "Спасибо за заявку.", prep: "Ваш кабинет готовится.", body: "Команда проверяет заявку. Доступ и инструкции придут на почту в течение 24 часов.", s1: "Проверьте почту — там будет ответ.", s2: "Подпишите договор и выберите план.", s3: "Slack-инвайт + первые аккаунты за < 5 мин.", back: "На главную", out: "Выйти" },
        tr: { received: "Başvuru alındı", thanks: user ? `Teşekkürler, ${user.full_name.split(" ")[0]}.` : "Başvurun için teşekkürler.", prep: "Panelin hazırlanıyor.", body: "Ekibimiz başvurunu inceliyor. Panel erişimi ve onboarding adımları 24 saat içinde e-posta ile paylaşılacak.", s1: "İnceleme e-postamız için gelen kutunu kontrol et.", s2: "Sözleşmeyi tamamla, planını seç.", s3: "Slack daveti + ilk hesaplar < 5 dk.", back: "Ana sayfaya dön", out: "Çıkış yap" },
    };
    const c = copy[language] || copy.en;

    return (
        <main
            data-testid="pending-page"
            className="min-h-screen grid place-items-center px-6 py-16"
        >
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="absolute inset-0 noise pointer-events-none" />

            <div className="relative w-full max-w-2xl bg-card border border-white/[0.08] rounded-3xl p-10 md:p-14 text-center space-y-7">
                <div className="flex justify-center">
                    <SkalaLogo size="lg" />
                </div>

                <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 grid place-items-center glow-primary">
                    <Check className="w-7 h-7 text-primary" />
                </div>

                <div className="space-y-3">
                    <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary">
                        {c.received}
                    </p>
                    <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">
                        {c.thanks}
                        <br />
                        <span className="text-gradient-primary">{c.prep}</span>
                    </h1>
                    <p className="text-muted-foreground max-w-md mx-auto">
                        {c.body}
                    </p>
                </div>

                <div className="grid sm:grid-cols-3 gap-3 pt-6 text-left">
                    <div className="bg-background/50 border border-white/5 rounded-xl p-4">
                        <Mail className="w-4 h-4 text-primary mb-2" />
                        <p className="text-xs font-semibold mb-0.5">Step 1</p>
                        <p className="text-xs text-muted-foreground">{c.s1}</p>
                    </div>
                    <div className="bg-background/50 border border-white/5 rounded-xl p-4">
                        <ShieldCheck className="w-4 h-4 text-primary mb-2" />
                        <p className="text-xs font-semibold mb-0.5">Step 2</p>
                        <p className="text-xs text-muted-foreground">{c.s2}</p>
                    </div>
                    <div className="bg-background/50 border border-white/5 rounded-xl p-4">
                        <MessageSquare className="w-4 h-4 text-primary mb-2" />
                        <p className="text-xs font-semibold mb-0.5">Step 3</p>
                        <p className="text-xs text-muted-foreground">{c.s3}</p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                    <Link to="/" data-testid="pending-back-home">
                        <Button
                            variant="outline"
                            className="border-white/10 bg-white/[0.02] hover:bg-white/[0.06]"
                        >
                            {c.back}
                        </Button>
                    </Link>
                    {user && (
                        <Button
                            data-testid="pending-logout"
                            onClick={logout}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        >
                            {c.out}
                        </Button>
                    )}
                </div>
            </div>
        </main>
    );
}
